import { useId } from 'react';
import type { Side } from '../../../engine';

/**
 * What a pawn wears in the Long Night (docs/LONG_NIGHT_ART_DIRECTION.md §12.10): its statuses as
 * badges (Boons as light marks round its base, Banes as dark marks on its body; at most two, then
 * "+n"), its Carvings as lines engraved into the lower half of its body like scrimshaw, a
 * Hallmark as a tiny gold punch on its base, and a seam of gold across it for each time it came
 * back from the Broker (three at most are drawn).
 */

export const BANES: ReadonlySet<string> = new Set([
  'pinned',
  'exposed',
  'cursed',
  'smoldering',
  'hollow',
  'heavy',
]);

/** One small mark per status, on a 20 × 20 grid. */
const BADGE: Record<string, string> = {
  warded: 'M10 3l5 2v4c0 4-2.6 6-5 7-2.4-1-5-3-5-7V5z',
  hasted: 'M3 6h9M5 10h11M3 14h9',
  kindled: 'M10 2c3 4 5 6 5 9a5 5 0 0 1-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3-1-5 1-8z',
  phased: 'M10 3a7 7 0 1 0 .01 0z',
  gilded: 'M3 13a7 3 0 1 0 14 0a7 3 0 1 0-14 0z',
  pinned: 'M10 1v16M6 4h8M10 17l-1.5 2h3z',
  exposed: 'M10 3l5 2v4c0 4-2.6 6-5 7-2.4-1-5-3-5-7V5zM10 4l-1.5 4 2.5 2-2 5',
  cursed: 'M4 12c0-4 3-6 6-6s6 2 6 6c-2-2-4-1-6 1-2-2-4-3-6-1z',
  smoldering: 'M3 17c4-1 5-5 9-6s3-4 4-6M15 3.5a1.6 1.6 0 1 0 .01 0z',
  hollow: 'M10 4a6 6 0 1 0 .01 0z',
  heavy: 'M6 9h8l2.5 8h-13zM8 9a2 2 0 1 1 4 0',
};

/** Fills that read as shapes; the rest are drawn as lines. */
const FILLED = new Set(['warded', 'kindled', 'cursed', 'heavy']);

/** A Carving's lines: one pattern each, chosen by its name, engraved below the middle. */
const PATTERNS = [
  'M36 60 l5 6 l5 -6 l5 6 l5 -6 l5 6 l5 -6',
  'M35 62 c4 -4 8 4 12 0 s8 4 12 0 s8 4 10 0',
  'M38 58 l24 12 M38 70 l24 -12',
  'M40 60 h4 M48 60 h4 M56 60 h4 M44 66 h4 M52 66 h4',
  'M36 64 l7 -6 l7 6 l7 -6 l7 6 M40 70 l7 -6 l7 6 l7 -6',
  'M50 64 m-6 0 a6 4 0 1 0 12 0 a6 4 0 1 0 -12 0 M50 64 m-2.5 0 a2.5 1.6 0 1 0 5 0',
];

const patternOf = (id: string): string => {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PATTERNS[h % PATTERNS.length]!;
};

/** Engraved into the body, so they float with it: Carvings, seams of gold, a Hallmark, Gilded. */
export function BodyMarks({
  side,
  carvings,
  seams = 0,
  hallmark = false,
  gilded = false,
}: {
  side: Side;
  carvings?: readonly { readonly id: string }[];
  seams?: number;
  hallmark?: boolean;
  gilded?: boolean;
}) {
  const clip = `pm${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const lines = (carvings ?? []).slice(0, 2);
  const seamCount = Math.min(3, seams);
  if (!lines.length && !seamCount && !hallmark && !gilded) return null;
  return (
    <svg className="pm-body pawn-svg" viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <clipPath id={clip}>
          {side === 'S' ? (
            <circle cx="50" cy="52" r="23" />
          ) : (
            <path d="M50 18 L70 46 L60 78 L40 78 L30 46Z" />
          )}
        </clipPath>
      </defs>
      <g clipPath={`url(#${clip})`}>
        {lines.map((c, i) => (
          <path
            key={c.id}
            className="pm-carving"
            d={patternOf(c.id)}
            transform={i ? 'translate(0 8)' : undefined}
          />
        ))}
        {Array.from({ length: seamCount }, (_, i) => (
          <path
            key={i}
            className="pm-seam"
            d={
              [
                'M26 38 l10 6 l6 -3 l9 8 l7 -2 l14 10',
                'M30 66 l9 -6 l7 4 l8 -9 l8 3 l10 -8',
                'M44 22 l3 10 l-4 7 l6 9 l-3 8 l5 12',
              ][i]
            }
          />
        ))}
      </g>
      {gilded && <ellipse className="pm-gilded" cx="50" cy="78" rx="18" ry="3.6" />}
      {hallmark && <path className="pm-hallmark" d="M50 70 l3 3 l-3 3 l-3 -3z" />}
    </svg>
  );
}

/** Statuses as badges: Boons light round the base, Banes dark on the body; two, then "+n". */
export function StatusBadges({
  statuses,
}: {
  statuses?: readonly { readonly kind: string; readonly n?: number }[];
}) {
  const list = statuses ?? [];
  if (!list.length) return null;
  const shown = list.slice(0, 2);
  const more = list.length - shown.length;
  return (
    <>
      {shown.map((st, i) => {
        const bane = BANES.has(st.kind);
        return (
          <span
            key={st.kind}
            className={`pm-badge ${bane ? 'is-bane' : 'is-boon'} at-${i}`}
            aria-hidden="true"
          >
            <svg viewBox="0 0 20 20">
              <path
                d={BADGE[st.kind] ?? 'M10 4a6 6 0 1 0 .01 0z'}
                className={FILLED.has(st.kind) ? 'is-filled' : ''}
              />
            </svg>
            {st.n !== undefined && st.n > 1 && <b>{st.n}</b>}
          </span>
        );
      })}
      {more > 0 && (
        <span className="pm-more" aria-hidden="true">
          +{more}
        </span>
      )}
    </>
  );
}
